import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AdminSidebar } from '@/components/AdminSidebar';
import { verifyAdmin, getAnalyticsOverview, getRecentVisitors } from '@/lib/api';
import { toast } from 'sonner';

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: '7 Days' },
  { key: 'month', label: '30 Days' },
  { key: 'all', label: 'All Time' },
];

const BarList = ({ title, items, testId }) => {
  const max = Math.max(...(items || []).map((i) => i.visits), 1);
  return (
    <div className="bg-surface border border-zinc-800 p-6" data-testid={testId}>
      <p className="font-mono text-xs text-text-secondary uppercase mb-4">{title}</p>
      {(!items || items.length === 0) && <p className="font-body text-sm text-text-secondary">No data yet</p>}
      <div className="space-y-3">
        {(items || []).map((item) => (
          <div key={item.label}>
            <div className="flex justify-between text-sm mb-1">
              <span className="font-body text-white truncate pr-2">{item.label}</span>
              <span className="font-mono text-text-secondary">{item.visits}</span>
            </div>
            <div className="h-1.5 bg-zinc-800">
              <div className="h-full bg-[#CCFF00]" style={{ width: `${(item.visits / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, accent, testId }) => (
  <div className="bg-surface border border-zinc-800 p-6" data-testid={testId}>
    <p className="font-mono text-xs text-text-secondary uppercase">{label}</p>
    <p className={`font-heading text-3xl font-black mt-2 ${accent ? 'text-[#CCFF00]' : 'text-white'}`}>{value}</p>
  </div>
);

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [overview, setOverview] = useState(null);
  const [visitors, setVisitors] = useState([]);

  const fetchData = useCallback(async (selectedPeriod) => {
    try {
      const [ov, recent] = await Promise.all([getAnalyticsOverview(selectedPeriod), getRecentVisitors(50)]);
      setOverview(ov);
      setVisitors(recent);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const user = await verifyAdmin();
      if (!user) {
        navigate('/admin');
        return;
      }
      if (user.role && user.role !== 'admin') {
        navigate('/admin/dashboard');
        return;
      }
      fetchData(period);
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    const interval = setInterval(() => fetchData(period), 30000);
    return () => clearInterval(interval);
  }, [period, fetchData]);

  const handlePeriodChange = (p) => {
    setPeriod(p);
    fetchData(p);
  };

  const exportCSV = async () => {
    let rowsData = visitors;
    try {
      rowsData = await getRecentVisitors(500);
    } catch (error) {
      toast.error('Using loaded data for export');
    }
    const headers = ['visited_at', 'page_url', 'page_title', 'country', 'city', 'browser', 'device', 'os', 'referrer', 'ip'];
    const rows = rowsData.map((v) => headers.map((h) => `"${String(v[h] ?? '').replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[headers.join(','), ...rows].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `bormula-visitors-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="admin-analytics-page">
      <AdminSidebar active="analytics" />

      <main className="lg:ml-64 p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-black text-white uppercase">Analytics</h1>
            <div className="flex items-center gap-2 mt-2">
              <Radio className="w-4 h-4 text-[#CCFF00] animate-pulse" />
              <span className="font-mono text-sm text-[#CCFF00]" data-testid="live-visitor-count">
                {overview?.live_visitors ?? 0} live now
              </span>
              <span className="font-mono text-xs text-text-secondary">(on the site right now, refreshes every 30s)</span>
            </div>
          </div>
          <Button onClick={exportCSV} variant="outline" className="border-zinc-800 text-white hover:bg-[#CCFF00] hover:text-black hover:border-[#CCFF00]" data-testid="export-csv-button">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>

        <div className="flex gap-2 mb-8 flex-wrap">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => handlePeriodChange(p.key)}
              className={`px-4 py-2 text-xs font-black uppercase tracking-widest border transition-colors ${
                period === p.key ? 'bg-[#CCFF00] text-black border-[#CCFF00]' : 'text-white/60 border-zinc-800 hover:text-white hover:border-zinc-600'
              }`}
              data-testid={`period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Page Views" value={(overview?.total_views ?? 0).toLocaleString()} accent testId="stat-page-views" />
          <StatCard label="Unique Visitors" value={(overview?.unique_visitors ?? 0).toLocaleString()} testId="stat-unique-visitors" />
          <StatCard label="Unique IPs" value={(overview?.unique_ips ?? 0).toLocaleString()} testId="stat-unique-ips" />
          <StatCard label="Avg Views / Day" value={overview?.avg_views_per_day ?? 0} testId="stat-avg-views" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <BarList title="Daily Views" items={(overview?.daily_views || []).map((d) => ({ label: d.date, visits: d.visits }))} testId="chart-daily-views" />
          <BarList title="Top Pages" items={overview?.top_pages} testId="chart-top-pages" />
          <BarList title="Top Countries" items={overview?.top_countries} testId="chart-top-countries" />
          <BarList title="Referrers" items={overview?.top_referrers} testId="chart-referrers" />
          <BarList title="Browsers" items={overview?.top_browsers} testId="chart-browsers" />
          <BarList title="Devices" items={overview?.top_devices} testId="chart-devices" />
        </div>

        <h2 className="font-heading text-xl font-black text-white uppercase mb-4">Recent Visitors</h2>
        <div className="bg-surface border border-zinc-800 overflow-x-auto" data-testid="recent-visitors-table">
          {visitors.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left p-3 font-mono text-xs text-text-secondary uppercase">Time</th>
                  <th className="text-left p-3 font-mono text-xs text-text-secondary uppercase">Page</th>
                  <th className="text-left p-3 font-mono text-xs text-text-secondary uppercase">Location</th>
                  <th className="text-left p-3 font-mono text-xs text-text-secondary uppercase">Browser</th>
                  <th className="text-left p-3 font-mono text-xs text-text-secondary uppercase">Device</th>
                  <th className="text-left p-3 font-mono text-xs text-text-secondary uppercase">Referrer</th>
                </tr>
              </thead>
              <tbody>
                {visitors.map((v) => (
                  <tr key={v.id} className="border-b border-zinc-800/50">
                    <td className="p-3 font-mono text-xs text-text-secondary whitespace-nowrap">{new Date(v.visited_at).toLocaleString()}</td>
                    <td className="p-3 font-body text-sm text-white max-w-[200px] truncate">{v.page_url}</td>
                    <td className="p-3 font-body text-sm text-text-secondary whitespace-nowrap">{[v.city, v.country].filter(Boolean).join(', ') || 'Unknown'}</td>
                    <td className="p-3 font-body text-sm text-text-secondary">{v.browser} / {v.os}</td>
                    <td className="p-3 font-body text-sm text-text-secondary">{v.device}</td>
                    <td className="p-3 font-body text-sm text-text-secondary max-w-[180px] truncate">{v.referrer || 'Direct'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="p-8 text-center font-body text-text-secondary">No visits tracked yet. Every visitor is counted anonymously the moment they land on the site.</p>
          )}
        </div>
      </main>
    </div>
  );
}
